import React, { Fragment, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl/dist';

// Components

import Form from '../../../lib/wrappers/Form';
import InstructionalText from '../notifications/InstructionalText';
import UniversalInput from '../../../lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// ActionCreators

import { changeData } from '../../../actions/ssap/ssapActionCreators';

// Utils

import { cloneObject } from '../../../utils/miscellaneous/cloneObject';
import { dataSyncingTools } from '../../../utils/tools/dataSyncingTools';
import { normalizeNameOfPerson } from '../../../utils/normalize/normalizeNameOfPerson';
import { getCoverageProgramName } from '../../../utils/normalize/getCoverageProgramName';
import { preventerTools } from '../../../utils/tools/preventerTools';
import { subtractFromDateWithFormat } from '../../../utils/dates/date.utils';

// Constants

import { DROPDOWN_OPTIONS } from '../../../constants/ssap/hhmTypes/dropdownOptions';
import { EMPTY_OBJECTS } from '../../../constants/ssap/initialValues/emptyObjects';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '../../../constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '../../../constants/inputs/validationErrorMessages';
import { STATE_NAMES } from '../../../constants/configs/states';
import { YYYY } from '../../../utils/dates/dateFormats.utils';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Additional Information - Member Pregnant Details ' Page
 *
 */
const Page2_21= props => {

    const {
        data,
        data: {
            taxHousehold: {
                thCommonParams: {
                    hasSomeoneWithMedicaidChipDenial
                }
            }
        },
        environment: {
            localDates: {
                serverDate
            },
            stateCode
        },
        householdMembers,
        form,
        formName
    } = props;

    const intlApi = useIntl();

    const {
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    const onFieldChange_member = (...args) => {

        let [ newData, indexInArray, , [ name, value ]  ] = args;

        const newState = { ...data };

        newData = {
            ...newData,
            fiveYearBar: false
        };

        switch(true) {
            case name === 'medicaidChipDenial' && !value:
                newData = {
                    ...newData,
                    ...cloneObject(EMPTY_OBJECTS.MEDICAID_CHIP_DENIAL)
                };
                break;
            case name === 'medicaidDeniedDueToImmigration':
                newData.changeInImmigrationSince5Year = null;
                newData.changeInImmigrationSinceMedicaidDenial = null;
                break;
            case name === 'changeInImmigrationSince5Year':
                newData.changeInImmigrationSinceMedicaidDenial = null;
                break;
        }

        const valuesForWatch = [
            newData.medicaidDeniedDueToImmigration,
            newData.changeInImmigrationSince5Year,
            newData.changeInImmigrationSinceMedicaidDenial
        ];

        newData.fiveYearBar = newData.medicaidChipDenial && valuesForWatch.reduce((res, v, idx) => {
            if(v === false && idx === 0) {
                return false;
            }
            if(v === false) {
                return true;
            }
            return res;
        }, false);

        newState.taxHousehold.householdMember[indexInArray] = { ...newData };

        const syncedData = dataSyncingTools.syncThCommonProps(
            newState,
            { propName: 'chip' }
        );

        props.actions.changeData(syncedData);
    };

    const onFieldChange_none_of_above = (...args) => {

        const [ newData ] = args;

        const updateIndividualValues = hhm => {
            return {
                ...hhm,
                ...cloneObject(EMPTY_OBJECTS.MEDICAID_CHIP_DENIAL)
            };
        };

        const syncedData = dataSyncingTools.syncThCommonProps(
            newData,
            {
                customUpdater: updateIndividualValues,
                isCommon: true,
                propName: 'chip'
            }
        );

        props.actions.changeData(syncedData);
    };

    const replacements = useMemo(
        () => {
            return {
                stateMedicaidProgramName: intlApi.formatMessage({ id: getCoverageProgramName('MEDICAID') }),
                stateChipProgramName: intlApi.formatMessage({ id: getCoverageProgramName('CHIP') })
            };
        },
        [stateCode]
    );

    const getHouseholdMembers = (householdMembers) => {

        const combinedMembers = [];

        householdMembers.forEach((member, i) => {

            const {
                applicantGuid,
                name,
                medicaidChipDenial,
                medicaidDeniedDate,
                medicaidDeniedDueToImmigration,
                changeInImmigrationSince5Year,
                changeInImmigrationSinceMedicaidDenial,
                applyingForCoverageIndicator,
                citizenshipImmigrationStatus: {
                    citizenshipStatusIndicator
                }
            } = member;

            const memberName = normalizeNameOfPerson(name);
            const fiveYearsAgo = subtractFromDateWithFormat(5, 'year', YYYY, serverDate);

            combinedMembers.push(
                <Fragment key = { applicantGuid }>
                    {
                        applyingForCoverageIndicator &&

                        <Fragment>
                            <ul className="usa-list usa-list--unstyled">
                                <li>
                                    <UniversalInput
                                        label = { {
                                            text: 'common.input.label.non_internationalized_text',
                                            labelTextReplacements: {
                                                text: memberName
                                            }
                                        } }
                                        inputData = { {
                                            isInputRequired: false,
                                            type: INPUT_TYPES.CHECKBOX,
                                            name: 'medicaidChipDenial',
                                            classes: [''],
                                            checked: medicaidChipDenial,
                                            value: medicaidChipDenial,
                                            currentData: member,
                                            fields: [],
                                        } }
                                        inputActions = { {
                                            onFieldChange: onFieldChange_member
                                        } }
                                        indexInArray={ i }
                                    />
                                </li>
                            </ul>

                            {
                                medicaidChipDenial &&

                                <Fragment>
                                    <div className = 'grid-container' >
                                        <UniversalInput
                                            id = 'medicaid_chip_denial_information'
                                            label = { {
                                                labelClass: [ 'padding-0' ],
                                                text: 'ssap.input.label.when_denied_medicaid',
                                                labelTextReplacements: {
                                                    personName: memberName
                                                },
                                                dataInstructions: 'instructional_text.medicaid_chip_denial_information',
                                                replacements
                                            } }
                                            inputData = { {
                                                inputClass: [ '' ],
                                                isInputRequired: true,
                                                type: INPUT_TYPES.DATE_PICKER,
                                                name: 'medicaidDeniedDate',
                                                value: medicaidDeniedDate,
                                                valuesToMatch: serverDate,
                                                validationType: VALIDATION_TYPES.DATE_VALID_CHIP_DENIAL_DATE,
                                                currentData: member,
                                                fields: []
                                            } }
                                            inputActions = { {
                                                onFieldChange: onFieldChange_member
                                            } }
                                            errorClasses = { [ 'margin-l-0' ] }
                                            indexInArray = { i }
                                        />

                                        {
                                            !citizenshipStatusIndicator &&

                                            <UniversalInput
                                                legend = { {
                                                    legendText: 'ssap.input.legend.not_eligible_for_medicaid',
                                                    legendTextReplacements: {
                                                        personName: <strong>{ normalizeNameOfPerson(name) }</strong>,
                                                        fiveYearsAgo: <strong>{ fiveYearsAgo }</strong>
                                                    }
                                                } }
                                                label = { {
                                                    showRequiredIcon: true
                                                } }
                                                inputData = { {
                                                    isInputRequired: true,
                                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                                    name: 'medicaidDeniedDueToImmigration',
                                                    value: medicaidDeniedDueToImmigration,
                                                    options: DROPDOWN_OPTIONS.YES_NO,
                                                    validationType: VALIDATION_TYPES.CHOSEN,
                                                    validationErrorMessage: VALIDATION_ERROR_MESSAGES.MAKE_SELECTION,
                                                    currentData: member,
                                                    fields: []
                                                } }
                                                inputActions = { {
                                                    onFieldChange: onFieldChange_member
                                                } }
                                                errorClasses = { [ 'margin-l-0' ] }
                                                indexInArray = { i }
                                            />
                                        }

                                        {
                                            medicaidDeniedDueToImmigration &&

                                            <UniversalInput
                                                legend = { {
                                                    legendText: 'ssap.input.legend.change_in_immigration_status',
                                                    legendTextReplacements: {
                                                        personName: <strong>{ normalizeNameOfPerson(name) }</strong>,
                                                        fiveYearsAgo: <strong>{ fiveYearsAgo }</strong>
                                                    }
                                                } }
                                                label = { {
                                                    showRequiredIcon: true
                                                } }
                                                inputData = { {
                                                    isInputRequired: true,
                                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                                    name: 'changeInImmigrationSince5Year',
                                                    value: changeInImmigrationSince5Year,
                                                    options: DROPDOWN_OPTIONS.YES_NO,
                                                    validationType: VALIDATION_TYPES.CHOSEN,
                                                    validationErrorMessage: VALIDATION_ERROR_MESSAGES.MAKE_SELECTION,
                                                    currentData: member,
                                                    fields: []
                                                } }
                                                errorClasses = { [ 'margin-l-0' ] }
                                                inputActions = { {
                                                    onFieldChange: onFieldChange_member
                                                } }
                                                indexInArray = { i }
                                            />
                                        }

                                        {
                                            changeInImmigrationSince5Year &&

                                            <UniversalInput
                                                legend = { {
                                                    legendText: 'ssap.input.legend.change_after_check_up',
                                                    legendTextReplacements: {
                                                        personName: <strong>{ normalizeNameOfPerson(name) }</strong>,
                                                        stateName: STATE_NAMES[stateCode]
                                                    }
                                                } }
                                                label = { {
                                                    showRequiredIcon: true
                                                } }
                                                inputData = { {
                                                    isInputRequired: true,
                                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                                    name: 'changeInImmigrationSinceMedicaidDenial',
                                                    value: changeInImmigrationSinceMedicaidDenial,
                                                    options: DROPDOWN_OPTIONS.YES_NO,
                                                    validationType: VALIDATION_TYPES.CHOSEN,
                                                    validationErrorMessage: VALIDATION_ERROR_MESSAGES.MAKE_SELECTION,
                                                    currentData: member,
                                                    fields: []
                                                } }
                                                errorClasses = { [ 'margin-l-0' ] }
                                                inputActions = { {
                                                    onFieldChange: onFieldChange_member
                                                } }
                                                indexInArray = { i }
                                            />
                                        }
                                    </div>
                                </Fragment>
                            }
                        </Fragment>
                    }
                </Fragment>
            );
        });

        combinedMembers.push(
            <UniversalInput
                key = 'hasSomeoneWithMedicaidChipDenial'
                label = { {
                    text: 'ssap.input.label.none_of_above',
                    isLabelRequired: false
                } }
                inputData = { {
                    isInputRequired: false,
                    type: INPUT_TYPES.CHECKBOX,
                    name: 'hasSomeoneWithMedicaidChipDenial',
                    checked: hasSomeoneWithMedicaidChipDenial === false,
                    value: hasSomeoneWithMedicaidChipDenial === false,
                    currentData: cloneObject(data),
                    fields: [ 'taxHousehold', 'thCommonParams' ],
                    reversedValue: true
                } }
                inputActions={ {
                    onFieldChange: onFieldChange_none_of_above
                } }
            />
        );
        return combinedMembers;
    };

    const topLevelValidity = useMemo(
        () => {
            return householdMembers.some(hhm => hhm.medicaidChipDenial) || hasSomeoneWithMedicaidChipDenial === false;
        },
        [ householdMembers, hasSomeoneWithMedicaidChipDenial ]
    );

    return (
        <Fragment>
            <Form
                { ...{
                    formName,
                    topLevelValidity
                } }
            >
                <div
                    className = 'fade-in'
                    id = 'fhMoreInfo'
                >
                    <div className = 'subsection' >
                        <ValidationErrorMessage
                            isVisible = { isFormSubmitted && !topLevelValidity }
                            messageText = { VALIDATION_ERROR_MESSAGES.ANY_VALUE }
                            errorClasses = { [ 'margin-l-0' ] }
                        />
                        <InstructionalText
                            Component = {
                                <p data-instructions = 'instructional_text.medicaid_chip_denial_information.general_notice' >
                                    { 'ssap.family_household.medicaid.ninety_days' }
                                </p>
                            }
                            childrenReplacements = { replacements }
                            replacements = { replacements }
                            required
                        />
                        { getHouseholdMembers(householdMembers) }
                    </div>
                </div>
            </Form>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    data: state.ssap.data,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_21);
